import React from 'react';
import { ShieldCheck, Award, ExternalLink, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from 'zite-auth-sdk';

const ALLERGENS = [
  { emoji: '🌿', name: 'Celery', sources: 'Soups, stocks, spice mixes' },
  { emoji: '🦐', name: 'Crustaceans', sources: 'Prawns, crab, lobster' },
  { emoji: '🥚', name: 'Eggs', sources: 'Mayo, pasta, cakes' },
  { emoji: '🐟', name: 'Fish', sources: 'Sauces, dressings, Worcestershire' },
  { emoji: '🫘', name: 'Lupin', sources: 'Flour, bread, pastries' },
  { emoji: '🥛', name: 'Milk', sources: 'Butter, cream, cheese' },
  { emoji: '🐚', name: 'Molluscs', sources: 'Mussels, oysters, squid' },
  { emoji: '🌻', name: 'Mustard', sources: 'Dressings, curries, marinades' },
  { emoji: '🌰', name: 'Tree Nuts', sources: 'Almonds, cashews, walnuts' },
  { emoji: '🥜', name: 'Peanuts', sources: 'Satay, baked goods' },
  { emoji: '🌾', name: 'Sesame', sources: 'Bread, hummus, tahini' },
  { emoji: '🫘', name: 'Soya', sources: 'Tofu, soy sauce, miso' },
  { emoji: '🍇', name: 'Sulphites', sources: 'Wine, dried fruit, vinegar' },
  { emoji: '🌾', name: 'Wheat/Gluten', sources: 'Bread, pasta, flour, sauces' },
];

function TierCard({
  tier, icon: Icon, color, title, roles, description, link, linkLabel,
}: {
  tier: number; icon: React.ElementType; color: string; title: string; roles: string; description: string; link?: string; linkLabel?: string;
}) {
  return (
    <div className={`border rounded-xl p-4 ${color}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-background/80 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Tier {tier}</span>
          </div>
          <h4 className="text-sm font-bold text-foreground">{title}</h4>
          <p className="text-xs text-primary font-medium mt-0.5">{roles}</p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{description}</p>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" className="mt-3 h-7 text-xs gap-1.5">
                <ExternalLink className="w-3 h-3" />
                {linkLabel}
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FoodHygieneGuide() {
  const { user } = useAuth();
  const isKitchenOrManager = ['Manager', 'Admin'].includes(user?.role ?? '') ||
    ['Kitchen', 'Management'].includes(user?.department ?? '') ||
    (user?.skills ?? []).some(s => ['Chef', 'CDP', 'Sous Chef', 'Kitchen'].includes(s));

  return (
    <div className="my-4 space-y-4">
      <div>
        <h3 className="text-sm font-bold text-foreground mb-1">🍽️ Food Hygiene Training Requirements</h3>
        <p className="text-xs text-muted-foreground">Two levels of certification required depending on your role.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <TierCard
          tier={1} icon={ShieldCheck}
          color="border-border bg-muted/30"
          title="Food Hygiene Awareness"
          roles="All FOH, bar staff & temporary employees"
          description="General awareness covering food safety basics, personal hygiene, temperature control, cross-contamination, and allergen queries. Complete online — ask your manager to assign this."
        />
        <TierCard
          tier={2} icon={Award}
          color={`border-primary/30 ${isKitchenOrManager ? 'bg-primary/5' : 'bg-muted/30'}`}
          title="FSA Level 2 Food Hygiene Certificate"
          roles="Managers, supervisors & all kitchen staff"
          description="Official nationally recognised qualification required by UK law. Complete via the FSA Allergy Training website, then upload your certificate to the system under your profile."
          link="https://allergytraining.food.gov.uk/login/index.php"
          linkLabel="Go to FSA Training Portal"
        />
      </div>

      {isKitchenOrManager && (
        <div className="border border-primary/30 bg-primary/5 rounded-xl px-4 py-3 flex items-start gap-2">
          <Award className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-foreground">Your role requires the Tier 2 certificate</p>
            <p className="text-xs text-muted-foreground mt-0.5">Once completed, upload your certificate to your profile under Certifications &amp; Documents.</p>
          </div>
        </div>
      )}

      {/* Allergens */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h4 className="text-xs font-bold text-foreground uppercase tracking-wider">The 14 Major Allergens — Know Them All</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
          {ALLERGENS.map(a => (
            <div key={a.name} className="flex items-start gap-1.5 bg-muted/30 rounded-lg px-2 py-1.5">
              <span className="text-sm">{a.emoji}</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">{a.name}</p>
                <p className="text-[10px] text-muted-foreground leading-tight truncate">{a.sources}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">⚠️ Always ask about allergies before taking a food order. If unsure of ANY ingredient — always check with the kitchen. Never guess.</p>
        </div>
      </div>
    </div>
  );
}
